





























import {
  defineComponent,
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  provide,
  ref,
  set,
  watch,
} from "@vue/composition-api";
import { throttle } from "lodash-es";
import { CallOptions } from "@scrile/api-provider/dist/api/CallOptionsProvider";
import { isLikeNotificationDetails } from "@scrile/api-provider/dist/api/LikeProvider";
import { InputMessageSearch, isMessageSubjectDetails, Message } from "@scrile/api-provider/dist/api/MessagesProvider";
import { Notification } from "@scrile/api-provider/dist/api/NotificationsProvider";
import { PaymentPackage, PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import useAppointments from "@/hooks/useAppointments";
import useAuthUser from "@/hooks/useAuthUser";
import usePager from "@/hooks/usePager";
import usePaymentPackage from "@/hooks/usePaymentPackage";
import useRole from "@/hooks/useRole";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import config from "@/config";
import { EVENTS } from "@/const";
import { useCheckCreatorServices } from "@/features/CreatorService/hooks/useCheckCreatorServices";
import useConvertingFiles from "@/views/PageMessages/hooks/useConvertingFiles";
import MessagesView from "./components/MessagesView.vue";
import useMessageThread from "./hooks/useMessageThread";
import useStartAppointment from "./hooks/useStartAppointment";

export default defineComponent({
  name: "PageMessages",
  components: { MessagesView },
  props: {
    userId: { type: String, required: true },
  },
  setup(props, { root }) {
    const { authUserStrict, isCreator } = useAuthUser();
    const { findAll: findAllPaymentPackages } = usePaymentPackage();
    const { isBookingAvailable, checkBookingAvailable } = useAppointments();
    const { nearestAppointment, isAppointmentStart, isMessagingAppointmentStart } = useStartAppointment(
      computed(() => props.userId)
    );
    const {
      messageThread,
      opponent: opponentFromThread,
      minUnreadMessageId,
      opponentMinUnreadMessageId,
      getThreadByOpponent,
      toggleHideThread,
      togglePin,
    } = useMessageThread(computed(() => ({ opponentId: props.userId })));
    const { hasServices, checkServices } = useCheckCreatorServices(computed(() => props.userId));
    const { hasServices: hasOwnServices, checkServices: checkOwnServices } = useCheckCreatorServices(
      computed(() => authUserStrict.value.id)
    );

    const loading = ref(false);
    const allowResponse = ref(true);
    const opponent = ref<UserPublicData>();
    const paymentPackages = ref<PaymentPackage[]>([]);
    const userCallOptions = ref<CallOptions>();
    const messagesViewRef = ref();

    const providedData = ref<{ user?: UserPublicData }>({});

    const { isCreator: isOpponentCreator } = useRole(opponent);

    const {
      enableScroll,
      data: messagesSearchResult,
      items,
      getNextPage: getPrevMessages,
      getFirstPage: getMessages,
      removeItem,
      updateItem,
      addItem,
    } = usePager<Message, InputMessageSearch>(async (page, requestData) => {
      return providers.MessagesProvider.find({
        data: {
          threadId: messageThread.value?.id,
          sort: "TIME,DESC",
          page,
          ...requestData,
        },
      });
    });
    const messageList = computed(() => Array.from<Message>(items.value ?? []).reverse());

    useConvertingFiles(messageList, updateItem);

    const getOpponentUserInfo = async () => {
      const resp = await providers.UserPublicDataProvider.search({ data: { id: [props.userId] } });
      opponent.value = resp.result[0];
    };

    const getPaymentPackages = async () => {
      paymentPackages.value = await findAllPaymentPackages([PaymentPackageEnum.MESSAGE_ATTACHMENT]);
    };

    const getCallOptions = async () => {
      if (!config.featureCalling) return;
      userCallOptions.value = (await providers.CallOptionsProvider.find({ userIds: [props.userId] })).find(
        i => i.userId === props.userId
      );
    };

    const getMessageSettings = async (userId: string) => {
      const settings = await providers.MessageSettingsProvider.search({ userIds: [userId], size: 1 });
      allowResponse.value = settings.result.find(i => i.userId === userId)?.allowIncoming ?? true;
    };

    const markedRead = (message: Message) => {
      if (!messageThread.value) return;
      let id = Number(message.id);
      const minimumId = Number(minUnreadMessageId.value);
      if (minimumId > 0 || minimumId < id) {
        const lastMessage = messageList.value[messageList.value.length - 1];
        if (lastMessage.id === message.id) id = -1;
        set(messageThread.value, "minUnreadMessageId", id + 1 + "");
      }
    };

    const addMessage = async (message: Message) => {
      if (!messageThread.value || !messagesSearchResult.value) {
        await getThreadByOpponent(props.userId);
        return getMessages();
      } else {
        set(messageThread.value, "hidden", false);
        if (messageThread.value?.opponentMinUnreadMessageId === "0") {
          messageThread.value = { ...messageThread.value, opponentMinUnreadMessageId: message.id };
        }
        addItem(message, "start");
      }
    };

    const receivedMessage = async (message: Message) => {
      if (message.senderId === "0" && !messageThread.value) {
        await getThreadByOpponent(props.userId);
        await getMessages();
      }
      if (messageThread.value?.id !== message.threadId || messageList.value.find(m => m.id === message.id)) {
        return;
      }
      await addMessage(message);
      await nextTick();
      messagesViewRef.value?.receivedMessage();
      if (messageThread.value && messageThread.value.minUnreadMessageId === "0") {
        set(messageThread.value, "minUnreadMessageId", message.id);
      }
    };

    const updatePurchasedMessage = async (messageId: string) => {
      if (!messagesSearchResult.value) return;
      const response = await providers.MessagesProvider.find({ data: { ids: [messageId] } });
      const updatedMessage = response.result[0];
      updateItem(updatedMessage, (i1, i2) => i1.id === i2.id);
    };

    const removeMessage = (messageId: string) => {
      removeItem(i => i.id === messageId);
      if (messagesSearchResult.value?.result.length === 0) {
        messageThread.value = undefined;
        messagesSearchResult.value = undefined;
      }
    };

    const checkAppointments = async () => {
      if (!opponent.value || !isOpponentCreator.value || isCreator.value) return;
      await checkBookingAvailable(props.userId);
    };

    const updateLikedMessage = async (payload: { notification?: Notification; messageId?: string }) => {
      let updatedMessage;
      if (
        payload.notification &&
        isLikeNotificationDetails(payload.notification.details) &&
        isMessageSubjectDetails(payload.notification.details.content)
      ) {
        updatedMessage = payload.notification.details.content.message;
      }
      if (payload.messageId) {
        const response = await providers.MessagesProvider.find({ data: { ids: [payload.messageId] } });
        updatedMessage = response.result[0];
      }
      if (updatedMessage) {
        updateItem(updatedMessage, (i1, i2) => i1.id === i2.id);
      }
    };

    const getData = async () => {
      try {
        loading.value = true;
        providers.MessageThreadsProvider.setReceiveErrors(false);
        const promises: Promise<any>[] = [getCallOptions(), getMessageSettings(props.userId), getPaymentPackages()];
        if (!messageThread.value) {
          await getThreadByOpponent(props.userId);
          opponentFromThread.value && (opponent.value = opponentFromThread.value);
        }
        if (!opponent.value) {
          await getOpponentUserInfo();
        }
        if (!opponent.value) {
          root.$router.push({ name: "messageThreads" });
          return;
        }
        promises.push(checkAppointments());
        if (isOpponentCreator.value) {
          promises.push(checkServices());
        }
        if (isCreator.value) {
          promises.push(checkOwnServices());
        }
        if (messageThread.value) {
          promises.push(getMessages());
        }
        await Promise.all(promises);
      } catch (e) {
        console.error(e);
      } finally {
        loading.value = false;
      }
    };

    const markRead = throttle(
      (message: Message) => {
        emitter.emit(EVENTS.MESSAGES_MARK_READ, message);
      },
      1000,
      { leading: false }
    );

    provide("messageData", providedData.value);

    watch(opponent, () => {
      providedData.value.user = opponent.value;
    });

    getData();

    emitter.on<any>(EVENTS.MESSAGES_NEW_MESSAGE, receivedMessage);
    emitter.on<any>(EVENTS.MESSAGES_MARKED_READ, markedRead);
    emitter.on<any>(EVENTS.MESSAGES_UPDATE_MESSAGE, updatePurchasedMessage);
    emitter.on<any>(EVENTS.MESSAGES_LIKED_MESSAGE, updateLikedMessage);

    onMounted(() => {
      emitter.emit(EVENTS.MAIN_MENU_HIDE, true);
    });

    onBeforeUnmount(() => {
      emitter.off<any>(EVENTS.MESSAGES_NEW_MESSAGE, receivedMessage);
      emitter.off<any>(EVENTS.MESSAGES_MARKED_READ, markedRead);
      emitter.off<any>(EVENTS.MESSAGES_UPDATE_MESSAGE, updatePurchasedMessage);
      emitter.off<any>(EVENTS.MESSAGES_LIKED_MESSAGE, updateLikedMessage);
      emitter.emit(EVENTS.MAIN_MENU_HIDE, false);
    });

    return {
      messagesViewRef,
      loading,
      authUserStrict,
      messageThread,
      messageList,
      minUnreadMessageId,
      opponentMinUnreadMessageId,
      opponent,
      userCallOptions,
      enableScroll,
      paymentPackages,
      allowResponse,
      nearestAppointment,
      isAppointmentStart,
      isMessagingAppointmentStart,
      isBookingAvailable,
      hasServices,
      hasOwnServices,
      togglePin,
      markRead,
      removeMessage,
      getPrevMessages,
      addMessage,
      toggleHideThread,
    };
  },
});
